<template>
    <div>
      <AdminHeader />
  
      <!-- Page Header -->
      <div class="page-header">
        <div>
          <h5 class="page-title">Bank List</h5>
          <p class="page-sub">Manage List of banks</p>
        </div>
  
        <div class="page-actions">
          <div class="search-wrap">
            <font-awesome-icon icon="search" class="search-icon" />
            <input
              type="text"
              class="search-input"
              placeholder="Search banks..."
              v-model="tableData.search"
              @input="tableData.page = 1; getBanks()"
            />
          </div>

          <select v-model="tableData.country_code" @change="tableData.page = 1; getBanks()" class="filter-select">
            <option :value="['NG','GB']">Country</option>
            <option :value="['NG']">Nigeria</option>
            <option :value="['GB']">United Kingdom</option>
          </select>
  
          <select v-model="tableData.status" @change="tableData.page = 1; getBanks()" class="filter-select">
            <option :value="[0,1]">All Statuses</option>
            <option :value="[1]">Active</option>
            <option :value="[0]">Inactive</option>
          </select>
  
          <button class="btn-primary-action" @click="openCreate" :disabled="permission.create_bank != 1">
            <font-awesome-icon icon="plus" /> Create Bank
          </button>
        </div>
      </div>
  
      <!-- Data card -->
      <div class="data-card">
  
        <!-- Loading -->
        <div v-if="loading" class="state-block">
          <div class="spinner-border text-primary" style="width:3rem;height:3rem;" role="status"></div>
          <p class="mt-3 text-muted-cell">Loading banks...</p>
        </div>
  
        <!-- Empty -->
        <div v-else-if="banks.length === 0" class="state-block">
          <div class="state-icon state-icon--blue"><font-awesome-icon icon="university" /></div>
          <p class="state-title">No banks found</p>
          <p class="state-sub">Create your first bank to get started.</p>
          <button class="btn-primary-action mt-3" @click="openCreate" :disabled="permission.create_bank != 1">
            <font-awesome-icon icon="plus" /> Create Bank
          </button>
        </div>
  
        <template v-else>
  
          <!-- Toolbar -->
          <div class="table-toolbar">
            <span class="table-count">
              Showing <strong>{{ pagination.from }}–{{ pagination.to }}</strong>
              of <strong>{{ pagination.total }}</strong>
            </span>
            <div class="toolbar-right">
              <label class="table-count">Rows:</label>
              <select v-model.number="tableData.length" @change="tableData.page = 1; getBanks()" class="rows-select">
                <option v-for="n in [5,10,20,50,100,200,500,1000]" :key="n" :value="n">{{ n }}</option>
              </select>
            </div>
          </div>
  
          <!-- Table -->
          <div class="table-wrap">
            <table class="data-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Bank Name</th>
                  <th>Bank Code</th>
                  <th>Country Code</th>
                  <th>Status</th>
                  <th class="col-actions">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(bank, index) in banks" :key="bank.id">
  
                  <td class="text-muted-cell">{{ pagination.from + index }}</td>
  
                  <td>
                    <div class="bank-cell">
                      <div class="bank-icon"><font-awesome-icon icon="university" /></div>
                      <span class="bank-name">{{ bank.name }}</span>
                    </div>
                  </td>
  
                  <td>
                    <span v-if="bank.code" class="code-badge">{{ bank.code }}</span>
                    <span v-else class="text-muted-cell">—</span>
                  </td>
  
                  <td class="text-muted-cell">{{ bank.country_code || '—' }}</td>
  
                  <td>
                    <label class="toggle-switch">
                      <input
                        type="checkbox"
                        :true-value="1"
                        :false-value="0"
                        v-model="statuses[index]"
                        :disabled="permission.update_bank != 1"
                        @change="updateBankStatus(bank.id)"
                      />
                      <span class="toggle-track"><span class="toggle-thumb"></span></span>
                      <span class="toggle-label" :class="statuses[index] ? 'on' : 'off'">
                        {{ statuses[index] ? 'Active' : 'Inactive' }}
                      </span>
                    </label>
                  </td>
  
                  <td class="col-actions">
                    <div class="actions-cell">
                      <button
                        class="action-btn action-btn--edit"
                        title="Edit"
                        :disabled="permission.update_bank != 1"
                        @click="openEdit(bank)"
                      >
                        <font-awesome-icon icon="pen" />
                      </button>
                      <button
                        class="action-btn action-btn--delete"
                        title="Delete"
                        :disabled="permission.delete_bank != 1"
                        @click="openDelete(bank)"
                      >
                        <font-awesome-icon icon="trash" />
                      </button>
                    </div>
                  </td>
  
                </tr>
              </tbody>
            </table>
          </div>
  
          <!-- Pagination -->
          <div class="table-footer">
            <pagination
              :pagination="pagination"
              @prev="tableData.page = pagination.currentPage - 1; getBanks()"
              @next="tableData.page = pagination.currentPage + 1; getBanks()"
            />
          </div>
  
        </template>
      </div>
  
      <!-- ── Create Modal ── -->
      <div class="modal-overlay" :class="{ open: createOpen }" @click.self="closeCreate">
        <div class="modal-box">
          <div class="modal-header-styled">
            <span class="modal-title-styled">Create Bank</span>
            <button class="modal-close-btn" @click="closeCreate"><font-awesome-icon icon="times" /></button>
          </div>
          <form @submit.prevent="createBank">
            <div class="modal-body-styled">
              <div class="fields-row">
                <div class="form-group-styled">
                  <label class="form-label-styled">Bank Name <span class="req">*</span></label>
                  <input v-model="name" type="text" class="form-input-styled" placeholder="Enter bank name" required />
                </div>
                <div class="form-group-styled">
                  <label class="form-label-styled">Bank Code</label>
                  <input v-model="code" type="text" class="form-input-styled" placeholder="e.g. 058" />
                </div>
              </div>
              <div class="fields-row">
                <div class="form-group-styled">
                  <label class="form-label-styled">Country</label>
                  <select v-model="country_code" class="form-input-styled" >
                    <option value="" disabled selected>-- select --</option>
                    <option value="NG">Nigeria</option>
                    <option value="GB">United Kingdom</option>
                  </select>
                </div>
              </div>
            </div>
            <div class="modal-footer-styled">
              <button type="button" class="btn-ghost" @click="closeCreate">Cancel</button>
              <button type="submit" class="btn-primary-action" :disabled="!name.trim() || crudLoading">
                <font-awesome-icon v-if="crudLoading" icon="spinner" spin class="me-1" />
                {{ crudLoading ? 'Creating...' : 'Create Bank' }}
              </button>
            </div>
          </form>
        </div>
      </div>
  
      <!-- ── Edit Modal ── -->
      <div class="modal-overlay" :class="{ open: editOpen }" @click.self="closeEdit">
        <div class="modal-box">
          <div class="modal-header-styled">
            <span class="modal-title-styled">Edit Bank</span>
            <button class="modal-close-btn" @click="closeEdit"><font-awesome-icon icon="times" /></button>
          </div>
          <form @submit.prevent="updateBank">
            <div class="modal-body-styled">
              <div class="fields-row">
                <div class="form-group-styled">
                  <label class="form-label-styled">Bank Name <span class="req">*</span></label>
                  <input v-model="edit_name" type="text" class="form-input-styled" placeholder="Enter bank name" required />
                </div>
                <div class="form-group-styled">
                  <label class="form-label-styled">Bank Code</label>
                  <input v-model="edit_code" type="text" class="form-input-styled" placeholder="e.g. 058" />
                </div>
              </div>
              <div class="fields-row">
                <div class="form-group-styled">
                  <label class="form-label-styled">Country</label>
                  <select v-model="edit_country_code" class="form-input-styled" >
                    <option value="" disabled selected>-- select --</option>
                    <option value="NG">Nigeria</option>
                    <option value="GB">United Kingdom</option>
                  </select>
                </div>
              </div>
            </div>
            <div class="modal-footer-styled">
              <button type="button" class="btn-ghost" @click="closeEdit">Cancel</button>
              <button type="submit" class="btn-primary-action" :disabled="!edit_name.trim() || crudLoading">
                <font-awesome-icon v-if="crudLoading" icon="spinner" spin class="me-1" />
                {{ crudLoading ? 'Saving...' : 'Save Changes' }}
              </button>
            </div>
          </form>
        </div>
      </div>
  
      <!-- ── Delete Modal ── -->
      <div class="modal-overlay" :class="{ open: deleteOpen }" @click.self="closeDelete">
        <div class="modal-box">
          <div class="modal-body-styled" style="padding:32px 24px 24px; text-align:center;">
            <div class="delete-icon-wrap"><font-awesome-icon icon="trash" style="font-size:26px;" /></div>
            <div class="delete-title">Delete Bank</div>
            <p class="delete-sub">
              Are you sure you want to delete <strong>{{ edit_name }}</strong>? This action cannot be undone.
            </p>
          </div>
          <div class="modal-footer-styled" style="justify-content:center; gap:12px;">
            <button type="button" class="btn-ghost" @click="closeDelete">Cancel</button>
            <button type="button" class="btn-danger-action" @click="deleteBank">Yes, Delete</button>
          </div>
        </div>
      </div>
  
      <!-- Backdrop -->
      <div v-if="createOpen || editOpen || deleteOpen" class="modal-backdrop-custom" />
  
    </div>
  </template>
  
  <script>
  import axios from 'axios';
  import { useToast } from 'vue-toastification';
  import AdminHeader from "./administrationheadertab.vue";
  import { useStore } from 'vuex';
  
  export default {
    name: 'BankList',
    components: { AdminHeader },
  
    data() {
      return {
        banks:    [],
        statuses: [],
        bank_id:  '',
        loading:     false,
        crudLoading: false,
  
        // Create fields
        name: '', code: '', country_code: '',
        // Edit fields
        edit_name: '', edit_code: '', edit_country_code: '',
  
        createOpen: false,
        editOpen:   false,
        deleteOpen: false,
  
        tableData: {
          draw: 0, length: 5, search: '', page: 1,
          column: 0, dir: 'desc', status: [0, 1], country_code: ['NG','GB'],
        },
  
        pagination: {
          lastPage: '', currentPage: '', total: '',
          lastPageUrl: '', nextPageUrl: '', prevPageUrl: '',
          from: 1, to: '',
        },
  
        toast: useToast(),
        store: useStore(),
      };
    },

    computed: {
      permission() {
        return this.store.state.getCurrentUserPermission;
      },
    },
  
    methods: {
      // ── Modal helpers ────────────────────────────────────────────────
      openCreate() {
        this.name = ''; this.code = ''; this.country_code = '';
        this.createOpen = true;
      },
      closeCreate() { this.createOpen = false; },
  
      openEdit(bank) {
        this.bank_id          = bank.id;
        this.edit_name   = bank.name;
        this.edit_code   = bank.code;
        this.edit_country_code = bank.country_code;
        this.editOpen = true;
      },
      closeEdit() { this.editOpen = false; },
  
      openDelete(bank) {
        this.bank_id        = bank.id;
        this.edit_name = bank.name;
        this.deleteOpen     = true;
      },
      closeDelete() { this.deleteOpen = false; },
  
      // ── Data ─────────────────────────────────────────────────────────
      async getBanks() {
        this.loading = true;
        this.tableData.draw++;
        try {
          const { data } = await axios.get('settings/allbank', { params: this.tableData });
          this.banks    = data.data.data;
          this.statuses = this.banks.map(b => b.is_active);
          this.configPagination(data.data);
        } catch (err) {
          this.toast.error(err.response?.data?.message ?? 'Could not load banks.');
        } finally {
          this.loading = false;
        }
      },
  
      async createBank() {
        if (!this.name.trim()) return;
        this.crudLoading = true;
        try {
          await axios.post('/settings/allbank', {
            name:    this.name,
            code:    this.code,
            country_code: this.country_code,
          });
          this.toast.success('Bank created successfully.');
          this.closeCreate();
          this.getBanks();
        } catch (err) {
          const status = err.response?.status;
          if (status === 422) this.toast.warning(err.response.data.message?.[0] ?? 'Invalid input.');
          else this.toast.error('An error occurred. Please try again.');
        } finally {
          this.crudLoading = false;
        }
      },
  
      async updateBank() {
        if (!this.edit_name.trim()) return;
        this.crudLoading = true;
        try {
          await axios.put('/settings/allbank/'+this.bank_id, {
            name:    this.edit_name,
            code:    this.edit_code,
            country_code: this.edit_country_code,
          });
          this.toast.success('Bank updated successfully.');
          this.closeEdit();
          this.getBanks();
        } catch (err) {
          const status = err.response?.status;
          if (status === 422) this.toast.warning('Invalid input. Please check and try again.');
          else if (status === 400) this.toast.error('This bank cannot be edited.');
          else this.toast.error('An error occurred. Please try again.');
        } finally {
          this.crudLoading = false;
        }
      },
  
      async updateBankStatus(id) {
        try {
          const { data } = await axios.put('settings/allbank/update-status/' + id);
          this.toast.success(data.message);
          this.getBanks();
        } catch (err) {
          const status = err.response?.status;
          if (status === 422) this.toast.warning('Invalid request.');
          else this.toast.error('Could not update status.');
        }
      },
  
      async deleteBank() {
        try {
          await axios.delete(`/settings/allbank/${this.bank_id}`);
          this.toast.success('Bank deleted successfully.');
          this.closeDelete();
          this.getBanks();
        } catch (err) {
          this.toast.error(err.response?.data?.message ?? 'Bank could not be deleted.');
        }
      },
  
      configPagination(data) {
        this.pagination.lastPage    = data.last_page;
        this.pagination.currentPage = data.current_page;
        this.pagination.total       = data.total;
        this.pagination.lastPageUrl = data.last_page_url;
        this.pagination.nextPageUrl = data.next_page_url;
        this.pagination.prevPageUrl = data.prev_page_url;
        this.pagination.from        = data.from;
        this.pagination.to          = data.to;
      },
    },
  
    mounted() { this.getBanks(); },
  };
  </script>
  
  <style scoped>
  /* ── Page ────────────────────────────────────────────────────────────── */
  .page-header  { display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:14px; margin-bottom:20px; }
  .page-title   { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
  .page-sub     { font-size:13px; color:#7a90b0; margin:0; }
  .page-actions { display:flex; align-items:center; flex-wrap:wrap; gap:8px; }
  
  /* ── Search & Filter ─────────────────────────────────────────────────── */
  .search-wrap  { position:relative; display:flex; align-items:center; }
  .search-icon  { position:absolute; left:11px; color:#7a90b0; font-size:13px; pointer-events:none; }
  .search-input { padding:8px 12px 8px 33px; border:1px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; background:#fff; width:200px; outline:none; transition:border-color .15s; }
  .search-input:focus { border-color:#004AAD; }
  .filter-select { padding:8px 12px; border:1px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; background:#fff; outline:none; cursor:pointer; }
  
  /* ── Buttons ─────────────────────────────────────────────────────────── */
  .btn-primary-action { display:inline-flex; align-items:center; gap:7px; background:#004AAD; color:#fff; font-size:13px; font-weight:600; padding:8px 16px; border-radius:8px; border:none; cursor:pointer; transition:background .15s; white-space:nowrap; }
  .btn-primary-action:hover:not(:disabled) { background:#003b8e; }
  .btn-primary-action:disabled { opacity:.55; cursor:not-allowed; }
  .btn-ghost { display:inline-flex; align-items:center; gap:7px; background:#f4f7fb; color:#3d5068; border:1px solid #e4eaf2; font-size:13px; font-weight:600; padding:9px 18px; border-radius:8px; cursor:pointer; transition:background .15s; }
  .btn-ghost:hover { background:#e4eaf2; }
  .btn-danger-action { display:inline-flex; align-items:center; gap:7px; background:#e03535; color:#fff; font-size:13px; font-weight:600; padding:9px 22px; border-radius:8px; border:none; cursor:pointer; transition:background .15s; }
  .btn-danger-action:hover { background:#c02828; }
  .mt-3 { margin-top:12px; }
  .me-1 { margin-right:4px; }
  
  /* ── Data card ───────────────────────────────────────────────────────── */
  .data-card { background:#fff; border:1px solid #e4eaf2; border-radius:12px; overflow:hidden; }
  
  /* ── States ──────────────────────────────────────────────────────────── */
  .state-block      { display:flex; flex-direction:column; align-items:center; justify-content:center; padding:64px 24px; text-align:center; }
  .state-icon       { width:60px; height:60px; border-radius:16px; display:flex; align-items:center; justify-content:center; font-size:24px; margin-bottom:16px; }
  .state-icon--blue { background:rgba(0,74,173,.08); color:#004AAD; }
  .state-title      { font-size:16px; font-weight:700; color:#1a2b45; margin:0 0 6px; }
  .state-sub        { font-size:13px; color:#7a90b0; margin:0; }
  .text-muted-cell  { color:#4a5e78; }
  
  /* ── Table toolbar ───────────────────────────────────────────────────── */
  .table-toolbar  { display:flex; align-items:center; justify-content:space-between; padding:14px 20px; border-bottom:1px solid #e4eaf2; flex-wrap:wrap; gap:10px; }
  .table-count    { font-size:13px; color:#7a90b0; }
  .table-count strong { color:#1a2b45; }
  .toolbar-right  { display:flex; align-items:center; gap:8px; }
  .rows-select    { border:1px solid #dde5f0; border-radius:7px; padding:5px 10px; font-size:13px; color:#1a2b45; background:#f8fafc; outline:none; cursor:pointer; }
  
  /* ── Table ───────────────────────────────────────────────────────────── */
  .table-wrap  { overflow-x:auto; }
  .data-table  { width:100%; border-collapse:collapse; }
  .data-table thead tr { background:#f8fafc; }
  .data-table thead th { font-size:11.5px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:#7a90b0; padding:12px 16px; border-bottom:1px solid #e4eaf2; white-space:nowrap; }
  .data-table tbody tr { border-bottom:1px solid #f0f4f8; transition:background .12s; }
  .data-table tbody tr:last-child { border-bottom:none; }
  .data-table tbody tr:hover { background:#f8fafc; }
  .data-table tbody td { padding:12px 16px; font-size:13px; vertical-align:middle; }
  .col-actions { width:90px; text-align:center; }
  
  /* ── Bank cell ───────────────────────────────────────────────────────── */
  .bank-cell { display:flex; align-items:center; gap:10px; }
  .bank-icon { width:30px; height:30px; border-radius:7px; background:rgba(0,74,173,.08); color:#004AAD; display:flex; align-items:center; justify-content:center; font-size:12px; flex-shrink:0; }
  .bank-name { font-size:13px; font-weight:500; color:#1a2b45; }
  
  /* ── Badges ──────────────────────────────────────────────────────────── */
  .code-badge { display:inline-block; font-family:monospace; font-size:12px; font-weight:700; background:#f4f7fb; color:#1a2b45; border:1px solid #e4eaf2; border-radius:6px; padding:2px 8px; }
  .acct-badge { display:inline-block; font-family:monospace; font-size:12px; font-weight:600; color:#3d5068; background:#f8fafc; border:1px solid #e4eaf2; border-radius:6px; padding:2px 8px; }
  
  /* ── Toggle switch ───────────────────────────────────────────────────── */
  .toggle-switch { display:inline-flex; align-items:center; gap:8px; cursor:pointer; user-select:none; }
  .toggle-switch input { display:none; }
  .toggle-track  { position:relative; width:36px; height:20px; border-radius:20px; background:#d1d9e8; transition:background .2s; flex-shrink:0; }
  .toggle-switch input:checked + .toggle-track { background:#16a34a; }
  .toggle-thumb  { position:absolute; top:3px; left:3px; width:14px; height:14px; border-radius:50%; background:#fff; box-shadow:0 1px 3px rgba(0,0,0,.2); transition:transform .2s; }
  .toggle-switch input:checked + .toggle-track .toggle-thumb { transform:translateX(16px); }
  .toggle-label  { font-size:12px; font-weight:600; }
  .toggle-label.on  { color:#16a34a; }
  .toggle-label.off { color:#7a90b0; }
  
  /* ── Action buttons ──────────────────────────────────────────────────── */
  .actions-cell      { display:flex; align-items:center; justify-content:center; gap:6px; }
  .action-btn        { width:30px; height:30px; border-radius:7px; border:1px solid #dde5f0; background:#fff; display:flex; align-items:center; justify-content:center; font-size:12px; color:#7a90b0; cursor:pointer; transition:all .15s; }
  .action-btn--edit:hover   { background:#e8f4fd; color:#004AAD; border-color:#c5d6f5; }
  .action-btn--delete:hover { background:#fff0f0; color:#e03535; border-color:#f5c6c6; }
  
  /* ── Table footer ────────────────────────────────────────────────────── */
  .table-footer { padding:14px 20px; border-top:1px solid #e4eaf2; display:flex; justify-content:flex-end; }
  
  /* ── Modal ───────────────────────────────────────────────────────────── */
  .modal-overlay         { position:fixed; inset:0; z-index:1050; display:flex; align-items:center; justify-content:center; opacity:0; pointer-events:none; transition:opacity .2s; }
  .modal-overlay.open    { opacity:1; pointer-events:all; }
  .modal-backdrop-custom { position:fixed; inset:0; z-index:1040; background:rgba(15,25,50,.45); backdrop-filter:blur(2px); }
  .modal-box             { background:#fff; border-radius:14px; width:100%; max-width:500px; margin:16px; box-shadow:0 20px 60px rgba(0,0,0,.18); overflow:hidden; transform:translateY(16px); transition:transform .22s ease; z-index:1051; position:relative; }
  .modal-overlay.open .modal-box { transform:translateY(0); }
  .modal-header-styled   { padding:20px 24px 16px; border-bottom:1px solid #e4eaf2; display:flex; align-items:center; justify-content:space-between; }
  .modal-title-styled    { font-size:16px; font-weight:700; color:#1a2b45; }
  .modal-close-btn       { width:30px; height:30px; border-radius:7px; border:1px solid #dde5f0; background:transparent; cursor:pointer; display:flex; align-items:center; justify-content:center; color:#7a90b0; font-size:13px; transition:background .15s; }
  .modal-close-btn:hover { background:#f4f7fb; }
  .modal-body-styled     { padding:20px 24px; display:flex; flex-direction:column; gap:14px; }
  .modal-footer-styled   { padding:16px 24px; border-top:1px solid #e4eaf2; display:flex; justify-content:flex-end; gap:10px; }
  
  /* ── Modal form ──────────────────────────────────────────────────────── */
  .fields-row { display:grid; grid-template-columns:1fr 1fr; gap:14px; }
  .form-group-styled  { display:flex; flex-direction:column; gap:6px; }
  .form-label-styled  { font-size:13px; font-weight:600; color:#1a2b45; }
  .req                { color:#e03535; }
  .form-input-styled  { width:100%; padding:9px 12px; border:1px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; background:#fff; outline:none; transition:border-color .15s, box-shadow .15s; font-family:inherit; }
  .form-input-styled:focus { border-color:#004AAD; box-shadow:0 0 0 3px rgba(0,74,173,.08); }
  
  /* ── Delete modal ────────────────────────────────────────────────────── */
  .delete-icon-wrap { width:64px; height:64px; border-radius:50%; background:#fff0f0; color:#e03535; display:flex; align-items:center; justify-content:center; margin:0 auto 16px; }
  .delete-title     { font-size:17px; font-weight:700; color:#1a2b45; margin-bottom:8px; }
  .delete-sub       { font-size:13px; color:#7a90b0; line-height:1.5; }
  
  /* ── Responsive ──────────────────────────────────────────────────────── */
  @media (max-width:768px) {
    .page-header, .page-actions { flex-direction:column; align-items:stretch; }
    .search-input { width:100%; }
    .fields-row   { grid-template-columns:1fr; }
  }
  </style>